import { usePasswordResetRequestMutation } from '@codezo/graphql';
import { useThemeColors } from '@codezo/theme';
import {
	Alert,
	Anchor,
	Box,
	Button,
	Group,
	Paper,
	Stack,
	Text,
	TextInput,
	Title,
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft, IconMail } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../components/common/Logo';

export function ForgotPasswordPage() {
	const [email, setEmail] = useState('');
	const [error, setError] = useState<string | null>(null);
	const navigate = useNavigate();

	const colors = useThemeColors();

	const [requestReset, { loading }] = usePasswordResetRequestMutation({
		onCompleted: (data) => {
			if (data.passwordResetRequest) {
				// Navigate directly to verification page with state
				navigate('/auth/reset-password/validate', {
					state: {
						token: data.passwordResetRequest,
						email,
					},
				});
			}
		},
		onError: (err) => {
			setError(err.message || 'Failed to request password reset. Please try again.');
		},
	});

	const handleSubmit = async () => {
		setError(null);

		try {
			await requestReset({
				variables: {
					input: {
						authKey: email,
					},
				},
			});
		} catch {
			// Error is already handled by onError callback
			// Error handled by mutation onError
		}
	};

	return (
		<Box style={{ position: 'relative' }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				<Paper
					p={{ base: 'xs', sm: 'xl' }}
					radius="md"
					w={{ base: 'calc(100vw - 20px)', xs: 480 }}
				>
					<Stack>
						<Stack gap="sm" ta="center">
							<Logo height={40} />
							<div>
								<Title mb={4}>Reset Password</Title>
								<Text size="sm" c={colors.textMuted}>
									Enter your email address and we&apos;ll send you a verification
									code
								</Text>
							</div>
						</Stack>

						{error && (
							<Alert
								icon={<IconAlertCircle size={20} color={colors.error} />}
								color="error"
							>
								{error}
							</Alert>
						)}

						<Stack gap="md">
							<TextInput
								label="Email"
								placeholder="your@email.com"
								leftSection={<IconMail size={18} />}
								value={email}
								onChange={(e) => setEmail(e.currentTarget.value)}
								disabled={loading}
								onKeyDown={(e) => {
									if (e.key === 'Enter' && email) {
										handleSubmit();
									}
								}}
							/>
						</Stack>

						<Button
							fullWidth
							onClick={handleSubmit}
							loading={loading}
							disabled={!email}
							size="sm"
						>
							Send Reset Code
						</Button>

						<Group justify="center">
							<Anchor
								component={Link}
								to="/auth/login"
								style={{ display: 'flex', alignItems: 'center', gap: 4 }}
							>
								<IconArrowLeft size={16} />
								Back to login
							</Anchor>
						</Group>
					</Stack>
				</Paper>
			</motion.div>
		</Box>
	);
}
